import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Layout.css";
import logo_img from "../public/img/LT_logo.svg";
import caret_icon from "../public/img/caret.svg";
import down_caret from "../public/img/down_caret.svg";
import settings_icon from "../public/img/settings.svg";
import admin_icon from "../public/img/admin.svg";
import plus_icon from "../public/img/plus.svg";
import edit_icon from "../public/img/edit.svg";
import lightbulb from "../public/img/lightbulb.svg";
import folder_icon from "../public/img/folder_icon.svg";
import history_icon from "../public/img/history_icon_light.svg";
import { useAuth } from "../contexts/AuthContext";
import { useChatbots } from "../contexts/ChatbotContext";
import { useWorkingChatbotIdSet } from "../contexts/UserStatusContext";

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, user, isCheckingAuth } = useAuth();
  const isGuest = user?.role === "Guest";
  const { loading, error, getRootChatbots, getChildChatbots, getAllChatbots } =
    useChatbots();

  const workingChatbotIds = useWorkingChatbotIdSet();

  // Recursive: returns true if any descendant chatbot of `parentId` is currently working.
  const isAnyDescendantWorking = useCallback(
    (parentId) => {
      const children = getChildChatbots(parentId);
      for (const child of children) {
        if (workingChatbotIds.has(String(child._id))) return true;
        if (isAnyDescendantWorking(child._id)) return true;
      }
      return false;
    },
    [getChildChatbots, workingChatbotIds],
  );

  // Custom hook to get the previous value of a prop/state
  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  // Track expanded parent tabs - Load from localStorage or default to empty object
  const [expandedParents, setExpandedParents] = useState(() => {
    const savedState = localStorage.getItem("expandedParents");
    return savedState ? JSON.parse(savedState) : {};
  });

  // Track if expansion was triggered by caret click for animation purposes
  const [caretClicked, setCaretClicked] = useState(false);

  // Initialize the sidebar state from localStorage or default to false (expanded)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const savedState = localStorage.getItem("sidebarCollapsed");
    return savedState !== null ? JSON.parse(savedState) : false;
  });
  const prevSidebarCollapsed = usePrevious(sidebarCollapsed);
  
  // Track if chat history sidebar is open (for active state on icon)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // Ref for sidebar element for click-outside detection
  const sidebarRef = useRef(null);
  
  // Track previous max depth to avoid unnecessary width changes
  const prevMaxDepthRef = useRef(0);

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Save expanded parents state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("expandedParents", JSON.stringify(expandedParents));
  }, [expandedParents]);

  // Listen for chat history state changes
  useEffect(() => {
    const handleHistoryStateChange = (e) => {
      setIsHistoryOpen(e.detail.isOpen);
    };
    
    const handleCloseChatbots = () => {
      if (!sidebarCollapsed) {
        setSidebarCollapsed(true);
      }
    };
    
    window.addEventListener('chatHistoryStateChange', handleHistoryStateChange);
    window.addEventListener('closeChatbotsSidebar', handleCloseChatbots);
    
    return () => {
      window.removeEventListener('chatHistoryStateChange', handleHistoryStateChange);
      window.removeEventListener('closeChatbotsSidebar', handleCloseChatbots);
    };
  }, [sidebarCollapsed]);

  // Update max depth CSS variable whenever expanded parents change
  useEffect(() => {
    if (loading) return;

    // Calculate the maximum depth of any expanded item
    let maxDepth = 0;

    const calculateMaxDepth = (items, currentDepth) => {
      items.forEach((item) => {
        if (expandedParents[item._id]) {
          maxDepth = Math.max(maxDepth, currentDepth);
          const children = getChildChatbots(item._id);
          if (children.length > 0) {
            calculateMaxDepth(children, currentDepth + 1);
          }
        }
      });
    };

    // Start from root items with depth 1
    calculateMaxDepth(getRootChatbots(), 1);

    // Only update CSS variable if max depth actually changed
    if (maxDepth !== prevMaxDepthRef.current) {
      const sidebar = document.querySelector(".dashboard-sidebar");
      if (sidebar) {
        sidebar.style.setProperty("--max-visible-depth", maxDepth.toString());
      }
      prevMaxDepthRef.current = maxDepth;
    }
  }, [expandedParents, loading, getChildChatbots, getRootChatbots]);

  // Toggle sidebar function
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Click outside to close sidebar on mobile/tablet
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Don't close if sidebar is already collapsed
      if (sidebarCollapsed) return;

      // For 630px and below (mobile with button)
      if (window.innerWidth <= 630) {
        // Exclude clicks on the mobile sidebar icon buttons
        if (e.target.closest('.mobile-sidebar-icon')) {
          return;
        }

        // Check if click is outside the sidebar
        if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
          setSidebarCollapsed(true);
        }
      }
      // For 631px to 850px (tablet overlay mode)
      else if (window.innerWidth <= 850) {
        // Check if click is outside the sidebar
        if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
          setSidebarCollapsed(true);
        }
      }
    };

    document.addEventListener("mousedown", handleGlobalClick);
    return () => {
      document.removeEventListener("mousedown", handleGlobalClick);
    };
  }, [sidebarCollapsed]);

  // Toggle parent expansion - updated to be more precise with expanding/collapsing
  const toggleParentExpansion = (parentId, event) => {
    // Prevent navigation when clicking the caret
    event.stopPropagation();
    event.preventDefault();

    // Set caret clicked flag to trigger animation
    setCaretClicked(true);

    // Toggle the expanded state for this specific parent
    setExpandedParents((prev) => {
      const newState = { ...prev };
      newState[parentId] = !prev[parentId];

      // If we're collapsing a parent, also collapse all its children
      // to ensure sidebar width returns to normal when not needed
      if (!newState[parentId]) {
        const childrenOfParent = getChildChatbots(parentId);
        childrenOfParent.forEach((child) => {
          if (getChildChatbots(child._id).length > 0) {
            newState[child._id] = false;
          }
        });
      }

      return newState;
    });

    // Reset caret clicked flag after animation completes
    setTimeout(() => setCaretClicked(false), 300);
  };

  // Get the base navigation path (slug) for an item
  const getItemPath = useCallback((item) => {
    if (!item) return "/";

    // Handle static paths first (e.g., for Admin, Settings)
    if (item.path && !item._id) {
      return item.path;
    }

    // Case 1: item.directory === false and item.component exists
    if (item.directory === false && item.component) {
      let slug = item.component
        .replace(/([A-Z])/g, "-$1")
        .replace(/^-/, "")
        .toLowerCase();
      // Ensure it doesn't start with multiple slashes if item.component accidentally had one
      if (slug.startsWith("/")) {
        slug = slug.substring(1);
      }
      return `/${slug}`; // Ensures one leading slash
    }

    // Case 2: item.directory === true
    if (item.directory === true) {
      return "/directory";
    }

    // Case 3: item.directory === false and item.component does not exist (default to /chat)
    if (item.directory === false && !item.component) {
      return "/chat";
    }

    // Fallback if no other conditions met (should be rare for DB items)
    return "/chat"; // Defaulting to /chat for items with _id but no other matching criteria
  }, []);

  // Get the full href path including query parameters
  const getFullHref = useCallback(
    (item) => {
      if (!item) return "/";

      const basePath = getItemPath(item);

      // Handle static paths (they already are full paths)
      if (item.path && !item._id) {
        return basePath;
      }

      // Case 2: item.directory === true (add ID query param)
      if (item.directory === true && item._id) {
        return `${basePath}?id=${item._id}`;
      }

      // Case 3: item.directory === false and !item.component (default to /chat, add ID query param)
      if (item.directory === false && !item.component && item._id) {
        return `${basePath}?id=${item._id}`;
      }

      // Case 1: item.directory === false and item.component exists (no ID query param needed for these slugs)
      // or any other case where _id is not applicable for query string.
      return basePath;
    },
    [getItemPath]
  );

  // Determine if an item's path (considering its type) is active
  const isActive = useCallback(
    (item) => {
      if (!item) return false;

      const currentPathname = location.pathname;
      const searchParams = new URLSearchParams(location.search);
      const activeIdFromQuery = searchParams.get("id");

      const itemBasePath = getItemPath(item);

      // Handle static paths first (e.g., for Admin, Settings)
      if (item.path && !item._id) {
        return currentPathname === item.path;
      }

      // Ensure item._id exists for database-driven items before proceeding
      if (!item._id) return false;

      // Case 1: item.directory === false and item.component exists
      if (item.directory === false && item.component) {
        return currentPathname === itemBasePath;
      }

      // Case 2: item.directory === true
      if (item.directory === true) {
        return (
          currentPathname === "/directory" && activeIdFromQuery === item._id
        );
      }

      // Case 3: item.directory === false and item.component does not exist (default to /chat)
      if (item.directory === false && !item.component) {
        return currentPathname === "/chat" && activeIdFromQuery === item._id;
      }

      return false; // Default to false
    },
    [location, getItemPath]
  );

  // Check if any child of a parent is active (recursive)
  const isAnyChildActive = useCallback(
    (parentId) => {
      const childChatbots = getChildChatbots(parentId);

      // Check if any direct child is active
      const anyDirectChildActive = childChatbots.some((child) =>
        isActive(child)
      );
      if (anyDirectChildActive) return true;

      // Check if any nested child is active (recursive)
      return childChatbots.some((child) => {
        // Check if this child has children and if any of them are active
        const childrenOfChild = getChildChatbots(child._id);
        return childrenOfChild.length > 0 && isAnyChildActive(child._id);
      });
    },
    [getChildChatbots, isActive]
  );

  // Handle navigation with support for nested items
  const handleNavigation = useCallback(
    (item) => {
      if (!item) return;

      // Close sidebar on mobile (630px and below) when navigating to any tab
      if (window.innerWidth <= 630) {
        setSidebarCollapsed(true);
      }
      // Close sidebar on tablet (631px to 850px) when navigating to a new tab  
      else if (window.innerWidth <= 850 && !sidebarCollapsed) {
        setSidebarCollapsed(true);
      }

      // Handle navigation for static items (like Admin, Settings, or logo)
      if (item.path && !item._id) {
        // Sidebar open/close logic for static items
        const isCurrentlyActiveForStatic = location.pathname === item.path;
        if (!sidebarCollapsed && isCurrentlyActiveForStatic) {
          // Removed auto-collapse behavior for already active items
          // setSidebarCollapsed(true);
        } else if (sidebarCollapsed && isCurrentlyActiveForStatic) {
          setSidebarCollapsed(false);
        }
        navigate(item.path);
        return;
      }

      // Ensure item._id exists for database-driven items
      if (!item._id) return;

      const isCurrentlyActive = isActive(item);

      if (!sidebarCollapsed && isCurrentlyActive) {
        // Removed auto-collapse behavior for already active items
        // setSidebarCollapsed(true);
      } else if (sidebarCollapsed && isCurrentlyActive) {
        setSidebarCollapsed(false);
      }

      // Recursive function to find and expand parent paths
      const expandParentPath = (currentId) => {
        // Iterate through all chatbots to find potential parents
        const allChatbots = getRootChatbots().concat(
          getChildChatbots("") // Get all non-root chatbots too
        );

        // Find parents that have this as a child
        for (const potentialParent of allChatbots) {
          const children = getChildChatbots(potentialParent._id);
          if (children.some((child) => child._id === currentId)) {
            // Found a parent - expand it only if not already expanded
            setExpandedParents((prev) => {
              if (prev[potentialParent._id]) {
                return prev; // Already expanded, don't update state
              }
              return {
                ...prev,
                [potentialParent._id]: true,
              };
            });
            expandParentPath(potentialParent._id);
            break;
          }
        }
      };

      // Check if the clicked item has children
      const hasChildren = getChildChatbots(item._id).length > 0;

      // If the item has children, expand it only if not already expanded
      if (hasChildren) {
        setExpandedParents((prev) => {
          if (prev[item._id]) {
            return prev; // Already expanded, don't update state
          }
          return { ...prev, [item._id]: true };
        });
      }

      // Otherwise, find and expand its parent path
      else {
        expandParentPath(item._id);
      }

      navigate(getFullHref(item));
    },
    [
      navigate,
      sidebarCollapsed,
      isActive,
      getChildChatbots,
      getRootChatbots,
      getFullHref,
      location.pathname,
    ]
  );

  // Check if the current user has access to the item.
  // Wrapped in useCallback so its identity is stable across renders — the
  // useEffect at line 677 depends on it, and a fresh function each render
  // would re-trigger that effect needlessly.
  const hasAccess = useCallback((item) => {
    if (!item || !item.access || !item.access.length) {
      return false;
    }

    // Always show items with debug info during development
    if (
      process.env.NODE_ENV === "development" &&
      item.access.includes("Debug")
    ) {
      return true;
    }

    // Guests: require explicit email or username-prefix access; ignore "All" or role-based access
    if (user?.role === "Guest") {
      if (!user?.email) return false;
      const prefix = user.email.split("@")[0];
      return item.access.includes(user.email) || item.access.includes(prefix);
    }

    // Everyone else: "All" grants access
    if (item.access.includes("All")) return true;

    // If auth is still being checked, or no user, deny access (unless "All" was present)
    if (isCheckingAuth || !user) {
      return false;
    }

    // At this point, user is definitely populated and auth checks are done.
    // If user role is "Top Manager", grant access to everything.
    if (user.role === "Top Manager") {
      return true;
    }

    // Check if user's email matches any access entry
    if (user.email && item.access.includes(user.email)) {
      return true;
    }

    // Check if user's role matches any access entry
    if (user.role && item.access.includes(user.role)) {
      return true;
    }

    // New check: if user's email prefix (username) matches any non-email/non-role access entry
    const userEmailPrefix = user.email.split("@")[0];
    const nonRoleAccessItems = item.access.filter(
      (acc) =>
        acc !== "Admin" &&
        acc !== "Super Admin" &&
        !acc.includes("@") &&
        acc !== "All"
    );

    if (nonRoleAccessItems.includes(userEmailPrefix)) {
      return true;
    }

    return false;
  }, [user, isCheckingAuth]);

  // Render SVG icon from string
  const renderSvgIcon = (svgString) => {
    if (!svgString) return null;

    return (
      <span
        className="sidebar-tab-icon"
        dangerouslySetInnerHTML={{ __html: svgString }}
      />
    );
  };

  // Initialize and manage expandedParents state based on sidebar state and active routes
  useEffect(() => {
    if (loading) return;

    setExpandedParents((prevExpandedFromStorageOrManualToggle) => {
      const rootItems = getRootChatbots();
      let newExpandedState = { ...prevExpandedFromStorageOrManualToggle }; // Start with current or stored state

      // Case 1: Sidebar just closed (was open, now is collapsed)
      if (prevSidebarCollapsed === false && sidebarCollapsed === true) {
        // Keep all expanded items expanded when closing so icons remain visible
        return newExpandedState;
      }
      // Case 2: Sidebar is open
      else if (!sidebarCollapsed) {
        // Respect manual expansions but ensure active items are expanded.
        rootItems.forEach((item) => {
          const children = getChildChatbots(item._id);
          if (children.length > 0) {
            if (isActive(item) || isAnyChildActive(item._id)) {
              // Pass item directly
              newExpandedState[item._id] = true; // Ensure active is expanded
            }
            // If not active, its manually toggled state (from prevExpandedFromStorageOrManualToggle) is preserved.
          }
        });
        return newExpandedState;
      }
      // Case 3: Sidebar was already closed and remains closed, or other scenarios (like initial load when collapsed)
      else if (sidebarCollapsed) {
        // (This also covers prevSidebarCollapsed === true && sidebarCollapsed === true,
        // and prevSidebarCollapsed === undefined && sidebarCollapsed === true for initial load)
        // More permissive: Keep existing expansions, only ensure the newly active parent is expanded.
        // This means we don't forcibly close other dropdowns if navigating while sidebar is already closed.
        rootItems.forEach((item) => {
          const children = getChildChatbots(item._id);
          if (children.length > 0) {
            if (isActive(item) || isAnyChildActive(item._id)) {
              // Pass item directly
              // If this parent (or its child) is now active, make sure it's expanded.
              newExpandedState[item._id] = true;
            }
            // Else, its existing state in newExpandedState (which started from prevExpanded) is preserved.
          }
        });
        return newExpandedState;
      }

      // Default return if none of the above conditions met (should be rare)
      return prevExpandedFromStorageOrManualToggle;
    });
  }, [
    sidebarCollapsed,
    prevSidebarCollapsed, // Added dependency
    location, // For isActive and isAnyChildActive
    loading,
    getRootChatbots,
    getChildChatbots,
    isActive, // Now a dependency
    isAnyChildActive, // Now a dependency
  ]);

  // No JS-based fade; CSS uses ellipsis now

  // Filter and render root level sidebar tabs
  const renderSidebarTabs = () => {
    const allRootChatbotsFromContext = getRootChatbots();
    const accessibleRootItems = allRootChatbotsFromContext.filter((item) =>
      hasAccess(item)
    );

    // Condition 1: Initial load and still loading, with no data yet fetched.
    if (loading && allRootChatbotsFromContext.length === 0 && !error) {
      return <div className="sidebar-loading">Loading tabs...</div>;
    }

    // Condition 2: Error occurred and we have no accessible items to show (either never loaded or filtered out).
    if (error && accessibleRootItems.length === 0) {
      return (
        <div className="sidebar-error">
          Error loading tabs. Please try again.
        </div>
      );
    }
    // If there was an error but we DO have stale data to show, we will proceed to show the stale data.

    // Condition 3: Data has been fetched (or is being refreshed), and there are no accessible items.
    if (!loading && !error && accessibleRootItems.length === 0) {
      return <div className="sidebar-no-items">No tools found.</div>;
    }

    // If we reach here, it means we have accessibleRootItems to render.
    // These items are based on the current chatbotState.chatbots in the context.
    // If a refresh is in progress (loading is true), these are "stale" items, but we show them.
    // Once the refresh completes, the context updates, this component re-renders,
    // and accessibleRootItems will be based on the "fresh" data.

    // Recursive function to render tabs at any level
    const renderTabsRecursively = (items, depth = 0) => {
      return items.map((item) => {
        const childChatbots = getChildChatbots(item._id).filter((child) =>
          hasAccess(child)
        );
        const hasChildren = childChatbots.length > 0;
        const itemHref = getFullHref(item);
        const isParentExpanded = expandedParents[item._id];
        const isCurrentActive = isActive(item);
        const isWorking =
          workingChatbotIds.has(String(item._id)) ||
          isAnyDescendantWorking(item._id);

        return (
          <div key={item._id} className="sidebar-tab-group" data-depth={depth}>
            <a
              href={itemHref}
              className={`sidebar-tab ${isCurrentActive ? "active" : ""} ${
                hasChildren ? "has-children" : ""
              } ${isWorking ? "working" : ""}`}
              data-depth={depth}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(item);
              }}
            >
              {renderSvgIcon(item.icon)}
              <div className="text-container">
                <span className="tab-text">{item.title}</span>
              </div>
              {hasChildren && (
                <div
                  className="parent-caret-container"
                  onClick={(e) => toggleParentExpansion(item._id, e)}
                >
                  <img
                    src={down_caret}
                    alt="Expand"
                    className={`parent-caret ${
                      isParentExpanded ? "expanded" : ""
                    } ${caretClicked ? "animate" : ""}`}
                  />
                </div>
              )}
              {isWorking && (
                <span className="tab-working-dot" aria-hidden="true" />
              )}
            </a>
            {hasChildren && (
              <div
                className={`sidebar-child-tabs ${
                  isParentExpanded ? "expanded" : "collapsed"
                } ${caretClicked ? "animate" : ""}`}
                data-depth={depth}
              >
                {/* Recursively render children of this item with increased depth */}
                {renderTabsRecursively(childChatbots, depth + 1)}
              </div>
            )}
          </div>
        );
      });
    };

    // Start rendering from root items
    return renderTabsRecursively(accessibleRootItems);
  };

  // Define static footer items
  const staticFooterItems = {
    admin: { path: "/admin", icon: admin_icon, title: "Admin Tools" },
    settings: { path: "/settings", icon: settings_icon, title: "Settings" },
    newTool: { path: "/create", icon: plus_icon, title: "New Tool" },
    myTools: { path: "/my-tools", icon: edit_icon, title: "My Tools" },
    // For logo navigation:
    home: { path: "/directory?id=681a7c84791eb9a98d7f4660" },
  };

  // Add debug method to log all access controls for chatbots (uncomment when debugging)
  useEffect(() => {
    // Ensure this runs only when relevant data has settled
    if (
      process.env.NODE_ENV === "development" &&
      !loading &&
      !error &&
      !isCheckingAuth
    ) {
      const allItems = getAllChatbots();
      if (allItems && allItems.length > 0) {
        allItems.forEach((item) => {
          hasAccess(item);
        });
      }
    }
  }, [loading, error, user, isCheckingAuth, getAllChatbots, hasAccess]); // Added hasAccess to dependencies

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-left">
          <div
            className="logo-container"
            onClick={() => handleNavigation(staticFooterItems.home)}
          >
            <img src={logo_img} alt="LT Logo" className="lt-logo" />
          </div>
          
          {/* Mobile sidebar icons (visible only on 630px and below) */}
          <button
            className={`mobile-sidebar-icon chatbots-icon ${!sidebarCollapsed ? 'active' : ''}`}
            onClick={() => {
              // Toggle sidebar - close if open, open if closed
              if (window.innerWidth <= 630) {
                // If opening chatbots sidebar, close history sidebar
                if (sidebarCollapsed && isHistoryOpen) {
                  window.dispatchEvent(new CustomEvent('closeChatHistory'));
                }
                toggleSidebar();
              }
            }}
            aria-label={sidebarCollapsed ? "Open chatbots" : "Close chatbots"}
          >
            <img src={folder_icon} alt="Chatbots" />
          </button>
          
          {/* Chat history icon - only show on /chat route */}
          {location.pathname === "/chat" && (
            <button
              className={`mobile-sidebar-icon history-icon ${isHistoryOpen ? 'active' : ''}`}
              onClick={() => {
                // Close chatbots sidebar if open
                if (window.innerWidth <= 630 && !sidebarCollapsed) {
                  setSidebarCollapsed(true);
                }
                // Dispatch custom event to toggle chat history
                window.dispatchEvent(new CustomEvent('toggleChatHistory'));
              }}
              aria-label="Toggle chat history"
            >
              <img src={history_icon} alt="History" />
            </button>
          )}
          
          <h1
            className={`app-title ${sidebarCollapsed ? "title-hidden" : ""}`}
          >
            AI Hub
          </h1>
        </div>
        <div className="header-right">
          <a
            href="/suggestion-box"
            className="idea-link"
            onClick={(e) => {
              e.preventDefault();
              navigate("/suggestion-box");
            }}
          >
            <img src={lightbulb} alt="Suggestion Box" className="idea-icon" />
            Have an idea for a tool?
          </a>
        </div>
      </header>

      <div className="content-wrapper">
        <aside
          ref={sidebarRef}
          className={`dashboard-sidebar ${sidebarCollapsed ? "collapsed" : ""}`}
        >
          <div className="sidebar-tabs">{renderSidebarTabs()}</div>

          <div className="sidebar-footer">
            {/* New Tool button */}
            {!isGuest && (
              <a
                href={staticFooterItems.newTool.path}
                className={`sidebar-tab ${
                  isActive(staticFooterItems.newTool) ? "active" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigation(staticFooterItems.newTool);
                }}
              >
                <span className="sidebar-tab-icon">
                  <img src={staticFooterItems.newTool.icon} alt="New Tool" />
                </span>
                <div className="text-container">
                  <span className="tab-text">
                    {staticFooterItems.newTool.title}
                  </span>
                </div>
              </a>
            )}

            {/* My Tools button - Added this new block */}
            {!isGuest && (
              <a
                href={staticFooterItems.myTools.path}
                className={`sidebar-tab ${
                  isActive(staticFooterItems.myTools) ? "active" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigation(staticFooterItems.myTools);
                }}
              >
                <span className="sidebar-tab-icon">
                  <img src={staticFooterItems.myTools.icon} alt="My Tools" />
                </span>
                <div className="text-container">
                  <span className="tab-text">
                    {staticFooterItems.myTools.title}
                  </span>
                </div>
              </a>
            )}

            {isAdmin && (
              <a
                href={staticFooterItems.admin.path}
                className={`sidebar-tab ${
                  isActive(staticFooterItems.admin) ? "active" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigation(staticFooterItems.admin);
                }}
              >
                <span className="sidebar-tab-icon">
                  <img src={staticFooterItems.admin.icon} alt="Admin" />
                </span>
                <div className="text-container">
                  <span className="tab-text">
                    {staticFooterItems.admin.title}
                  </span>
                </div>
              </a>
            )}

            <a
              href={staticFooterItems.settings.path}
              className={`sidebar-tab ${
                isActive(staticFooterItems.settings) ? "active" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(staticFooterItems.settings);
              }}
            >
              <span className="sidebar-tab-icon">
                <img src={staticFooterItems.settings.icon} alt="Settings" />
              </span>
              <div className="text-container">
                <span className="tab-text">
                  {staticFooterItems.settings.title}
                </span>
              </div>
            </a>

            <div className="toggle-button" onClick={toggleSidebar}>
              <img
                src={caret_icon}
                alt="Toggle Sidebar"
                className={`caret-icon ${sidebarCollapsed ? "flipped" : ""}`}
              />
            </div>
          </div>
        </aside>

        <main className="dashboard-main">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
